// src/pages/hr/recruitment/RecruitmentDashboard.jsx
import React from "react";
import { useRecruitmentContext } from "../../../context/RecruitmentContext";

export default function RecruitmentDashboard() {
  const {
    jobOpenings,
    candidates,
    offers,
    interviews,
    activityLogs,
    stages,
    stageColors,
  } = useRecruitmentContext();

  const openJobs = jobOpenings.filter((j) => j.status === "Open");
  const activeCandidates = candidates.filter(
    (c) => !["Rejected", "Withdrawn", "Hired"].includes(c.stage)
  );
  const hiredCount = candidates.filter((c) => c.stage === "Hired").length;
  const offerCount = offers.length;
  const acceptedOffers = offers.filter((o) => o.status === "Accepted").length;

  const todayStr = new Date().toISOString().split("T")[0];
  const todayInterviews = interviews.filter(
    (i) => i.scheduledDate === todayStr || i.date === todayStr
  );

  // Department breakdown
  const deptCounts = {};
  jobOpenings.forEach((j) => {
    const dept = j.department || "General";
    deptCounts[dept] = (deptCounts[dept] || 0) + 1;
  });
  const deptEntries = Object.entries(deptCounts);

  // Stage distribution
  const stageCounts = {};
  stages.forEach((s) => {
    stageCounts[s] = candidates.filter((c) => c.stage === s).length;
  });

  return (
    <div className="ats-dashboard" style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      <style>{`
        .ats-kpi-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: 1rem; }
        .ats-kpi-card { background: var(--bg-card, #fff); border: 1px solid var(--border, #e5e7eb); border-radius: 12px; padding: 1.1rem 1.2rem; box-shadow: 0 1px 5px rgba(0,0,0,0.05); }
        .ats-kpi-val { font-size: 1.8rem; font-weight: 800; color: var(--text-primary, #111); line-height: 1.1; }
        .ats-kpi-lbl { font-size: 0.78rem; font-weight: 600; color: var(--text-muted, #6b7280); margin-top: 4px; }
        .ats-kpi-sub { font-size: 0.72rem; color: #10b981; margin-top: 4px; font-weight: 500; }
        .ats-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 1.25rem; }
        @media (max-width: 850px) { .ats-grid-2 { grid-template-columns: 1fr; } }
        .ats-panel { background: var(--bg-card, #fff); border: 1px solid var(--border, #e5e7eb); border-radius: 12px; padding: 1.25rem 1.5rem; box-shadow: 0 1px 5px rgba(0,0,0,0.05); }
        .ats-panel-title { font-size: 0.95rem; font-weight: 700; color: var(--text-primary, #111); margin-bottom: 1rem; display: flex; justify-content: space-between; align-items: center; }
        .funnel-bar-row { display: flex; align-items: center; gap: 0.75rem; margin-bottom: 0.6rem; font-size: 0.82rem; }
        .funnel-lbl { width: 140px; font-weight: 600; color: var(--text-primary, #111); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .funnel-track { flex: 1; height: 10px; background: #f3f4f6; border-radius: 99px; overflow: hidden; }
        .funnel-fill { height: 100%; border-radius: 99px; transition: width 0.4s; }
        .funnel-cnt { width: 32px; text-align: right; font-weight: 700; font-size: 0.8rem; }
        .activity-row { display: flex; gap: 0.75rem; align-items: flex-start; padding: 0.65rem 0; border-bottom: 1px solid var(--border, #f3f4f6); font-size: 0.82rem; }
        .activity-row:last-child { border-bottom: none; }
        .activity-dot { width: 8px; height: 8px; border-radius: 50%; background: #1b4b31; margin-top: 5px; flex-shrink: 0; }
        .activity-time { font-size: 0.7rem; color: var(--text-muted, #6b7280); }
      `}</style>

      {/* KPI Cards */}
      <div className="ats-kpi-grid">
        <div className="ats-kpi-card" style={{ borderTop: "3px solid #1b4b31" }}>
          <div className="ats-kpi-val">{openJobs.length}</div>
          <div className="ats-kpi-lbl">Open Requisitions</div>
          <div className="ats-kpi-sub">Across {deptEntries.length} departments</div>
        </div>
        <div className="ats-kpi-card" style={{ borderTop: "3px solid #0ea5e9" }}>
          <div className="ats-kpi-val">{activeCandidates.length}</div>
          <div className="ats-kpi-lbl">Active Candidates</div>
          <div className="ats-kpi-sub">{candidates.length} total applications</div>
        </div>
        <div className="ats-kpi-card" style={{ borderTop: "3px solid #f59e0b" }}>
          <div className="ats-kpi-val">{todayInterviews.length}</div>
          <div className="ats-kpi-lbl">Interviews Today</div>
          <div className="ats-kpi-sub">{interviews.length} total scheduled</div>
        </div>
        <div className="ats-kpi-card" style={{ borderTop: "3px solid #10b981" }}>
          <div className="ats-kpi-val">{offerCount}</div>
          <div className="ats-kpi-lbl">Offers Released</div>
          <div className="ats-kpi-sub">{acceptedOffers} accepted</div>
        </div>
        <div className="ats-kpi-card" style={{ borderTop: "3px solid #8b5cf6" }}>
          <div className="ats-kpi-val">{hiredCount}</div>
          <div className="ats-kpi-lbl">Total Hired</div>
          <div className="ats-kpi-sub">Success conversion</div>
        </div>
      </div>

      {/* Two-column grid */}
      <div className="ats-grid-2">
        {/* Hiring Funnel by Pipeline Stage */}
        <div className="ats-panel">
          <div className="ats-panel-title">
            <span>📊 Hiring Pipeline Funnel</span>
            <span style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>Live Candidate Distribution</span>
          </div>
          {stages.map((stg) => {
            const count = stageCounts[stg] || 0;
            const max = Math.max(...Object.values(stageCounts), 1);
            const pct = Math.round((count / max) * 100);
            return (
              <div key={stg} className="funnel-bar-row">
                <div className="funnel-lbl">{stg}</div>
                <div className="funnel-track">
                  <div
                    className="funnel-fill"
                    style={{
                      width: `${pct}%`,
                      backgroundColor: stageColors[stg] || "#1b4b31",
                    }}
                  />
                </div>
                <div className="funnel-cnt" style={{ color: stageColors[stg] || "#1b4b31" }}>
                  {count}
                </div>
              </div>
            );
          })}
        </div>

        {/* Interviews & Department Breakdown */}
        <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
          <div className="ats-panel">
            <div className="ats-panel-title">
              <span>📅 Today's Interviews ({todayInterviews.length})</span>
            </div>
            {todayInterviews.length === 0 ? (
              <div style={{ color: "var(--text-muted)", fontSize: "0.83rem", textAlign: "center", padding: "1rem 0" }}>
                No interviews scheduled for today 🎉
              </div>
            ) : (
              todayInterviews.map((item) => (
                <div key={item.id} style={{ display: "flex", justifyContent: "space-between", padding: "0.5rem 0", borderBottom: "1px solid var(--border)", fontSize: "0.83rem" }}>
                  <div>
                    <div style={{ fontWeight: 600 }}>{item.candidateName || "Candidate"}</div>
                    <div style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>{item.round || "Round 1"} · {item.interviewerName || "Interviewer"}</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontWeight: 700, color: "#1b4b31" }}>{item.scheduledTime || item.time || "—"}</div>
                    {item.meetingLink && (
                      <a href={item.meetingLink} target="_blank" rel="noreferrer" style={{ fontSize: "0.72rem", color: "#0ea5e9", textDecoration: "underline" }}>
                        Join Link
                      </a>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="ats-panel">
            <div className="ats-panel-title">
              <span>🏢 Requisitions by Department</span>
            </div>
            {deptEntries.length === 0 ? (
              <div style={{ color: "var(--text-muted)", fontSize: "0.83rem", textAlign: "center", padding: "1rem 0" }}>
                No job requisitions created yet
              </div>
            ) : (
              deptEntries.map(([dept, cnt]) => (
                <div key={dept} className="funnel-bar-row">
                  <div className="funnel-lbl">{dept}</div>
                  <div className="funnel-track">
                    <div className="funnel-fill" style={{ width: `${(cnt / Math.max(...deptEntries.map(([, c]) => c), 1)) * 100}%`, background: "#1b4b31" }} />
                  </div>
                  <div className="funnel-cnt">{cnt}</div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Audit Log / Recent Activity */}
      <div className="ats-panel">
        <div className="ats-panel-title">
          <span>⚡ Recent Recruitment Activity</span>
          <span style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>Audit Trail</span>
        </div>
        {activityLogs.length === 0 ? (
          <div style={{ color: "var(--text-muted)", fontSize: "0.83rem", textAlign: "center", padding: "1rem 0" }}>
            No activity logged yet
          </div>
        ) : (
          activityLogs.slice(0, 6).map((act) => (
            <div key={act.id} className="activity-row">
              <div className="activity-dot" />
              <div style={{ flex: 1 }}>
                <span style={{ fontWeight: 600 }}>{act.actorName || "User"}</span>{" "}
                <span style={{ color: "var(--text-muted)" }}>
                  {act.action === "STAGE_MOVED"
                    ? `moved candidate "${act.entityName}" from ${act.oldValue} to ${act.newValue}`
                    : act.action || "performed an update"}
                </span>
              </div>
              <div className="activity-time">
                {act.createdAt?.toDate ? act.createdAt.toDate().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "Recently"}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
